import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { AuthState } from "./auth.types";
import {
  getCdnCookie,
  getCustomToken,
  getIdentity,
  getUserConnections,
  signOut,
  updateUserFlags,
} from "./auth.thunks";

const initialState: AuthState = {
  isLoading: {
    customToken: false,
    signIn: false,
    signOut: false,
    identity: false,
    userConnections: false,
    userFlags: false,
    cdnCookie: false,
  },
  isError: {
    customToken: false,
    signIn: false,
    signOut: false,
    identity: false,
    userConnections: false,
    userFlags: false,
    cdnCookie: false,
  },
  customToken: null,
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  org: null,
  userSignUpFlow: null,
  userConnections: null,
};

const authSlice = createSlice({
  initialState,
  name: "auth",
  reducers: {
    initAuth: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = true;
      state.isLoggedIn = state.isLoggedIn || action.payload;
    },
    startSignIn: (state) => {
      state.isError.signIn = false;
      state.isLoading.signIn = true;
    },
    endSignIn: (state) => {
      state.isLoading.signIn = false;
      state.customToken = null;
    },
    throwSignInError: (state) => {
      state.isError.signIn = true;
    },
    clearSignInError: (state) => {
      state.isError.signIn = false;
      state.isError.identity = false;
    },
    resetAuthState: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCustomToken.pending, (state) => {
      state.isLoading.customToken = true;
      state.isError.customToken = false;
    });
    builder.addCase(getCustomToken.fulfilled, (state, action) => {
      state.isLoading.customToken = false;
      state.customToken = action.payload;
    });
    builder.addCase(getCustomToken.rejected, (state, action) => {
      state.isLoading.customToken = false;
      state.isError.customToken = !action.meta.aborted;
    });
    builder.addCase(getIdentity.pending, (state) => {
      state.isLoading.identity = true;
      state.isError.identity = false;
    });
    builder.addCase(getIdentity.fulfilled, (state, action) => {
      state.isLoading.identity = false;
      state.user = action.payload.user;
      state.org = action.payload.org;
      state.userSignUpFlow = action.payload.userSignUpFlow;
    });
    builder.addCase(getIdentity.rejected, (state, action) => {
      state.isLoading.identity = false;
      state.isError.identity = !action.meta.aborted;
    });
    builder.addCase(getUserConnections.pending, (state) => {
      state.isLoading.userConnections = true;
      state.isError.userConnections = false;
    });
    builder.addCase(getUserConnections.fulfilled, (state, action) => {
      state.isLoading.userConnections = false;
      state.userConnections = action.payload;
    });
    builder.addCase(getUserConnections.rejected, (state, action) => {
      state.isLoading.userConnections = false;
      state.isError.userConnections = !action.meta.aborted;
    });
    builder.addCase(updateUserFlags.pending, (state) => {
      state.isLoading.userFlags = true;
      state.isError.userFlags = false;
    });
    builder.addCase(updateUserFlags.fulfilled, (state) => {
      state.isLoading.userFlags = false;
    });
    builder.addCase(updateUserFlags.rejected, (state, action) => {
      state.isLoading.userFlags = false;
      state.isError.userFlags = !action.meta.aborted;
    });
    builder.addCase(signOut.pending, (state) => {
      state.isLoading.signOut = true;
      state.isError.signOut = false;
      state.isLoggedIn = false;
      state.user = null;
      state.org = null;
      state.userSignUpFlow = null;
      state.userConnections = null;
    });
    builder.addCase(signOut.fulfilled, (state) => {
      state.isLoading.signOut = false;
    });
    builder.addCase(signOut.rejected, (state, action) => {
      state.isLoading.signOut = false;
      state.isError.signOut = !action.meta.aborted;
    });
    builder.addCase(getCdnCookie.pending, (state) => {
      state.isLoading.cdnCookie = true;
      state.isError.cdnCookie = false;
    });
    builder.addCase(getCdnCookie.fulfilled, (state) => {
      state.isLoading.cdnCookie = false;
    });
    builder.addCase(getCdnCookie.rejected, (state, action) => {
      state.isLoading.cdnCookie = false;
      state.isError.cdnCookie = !action.meta.aborted;
    });
  },
});

export const {
  initAuth,
  startSignIn,
  endSignIn,
  throwSignInError,
  clearSignInError,
  resetAuthState,
} = authSlice.actions;

export const authReducer = authSlice.reducer;
